// Record by hand (#152 B1): a certificate the intake job could not record - a scan, an unmatched email -
// recorded here by the person reading it, the scan beside the form.
//
// The scan is QA@'s attachment read as the person (GET /cofa-intake/{id}/file); Save is POST /cofas, the
// same record the gateway tool and the intake job make, as MANUAL_READ (rule 19: never a machine PASS).
// The server keeps the file, judges against the material's spec and a person decides on the receipt
// screen (rule 18). What is typed is what the certificate PRINTS: the lot as printed, never D365's batch.
// Rule 15: a spec that could not be read says so, and the form still takes results as printed.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { api, errorMessage } from '../../lib/api';
import { grantHint, useMyGrants } from '../../shells/OfficeShell';
import { FileViewer } from './CertificateFile';
import { SIGNATURE_LABEL, type IntakeRow, type ReceiptDetail, type RegisterPayload, type RegisterRow, type SignatureKind } from './goodsInModel';
import { applySuggestion, emptyForm, emptyLot, emptyResult, isUnchecked, problems, resultsFromSpec, suggestedPo, toBody, uncheckedAi, type ByHandForm, type SpecParameter, type Suggestion } from './recordByHandModel';

const SIGNATURE_KINDS: SignatureKind[] = ['PRINTED_NAME', 'ELECTRONIC_STATEMENT', 'IMAGE', 'NONE'];

export function RecordByHand({ row, onRecorded }: { row: IntakeRow; onRecorded: () => void }) {
  const [open, setOpen] = useState(false);
  const grants = useMyGrants();
  const hint = grantHint(grants, 'CAPTURE', 'Recording by hand');
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {/* (UI/UX audit, 31 Oct 2026, UX-45) One of up to twenty on the Intake list: the name says which
            attachment, so a screen reader hears more than twenty "Record by hand". */}
        <Button type="button" variant="outline" size="sm" aria-label={`Record ${row.attachmentName} by hand`} disabled={hint !== null} title={hint ?? undefined}>
          Record by hand
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-(--s2) overflow-y-auto sm:max-w-[min(1400px,96vw)]">
        <SheetHeader>
          <SheetTitle>Record by hand</SheetTitle>
          <SheetDescription>
            <span className="font-mono">{row.attachmentName}</span> · {row.senderAddress ?? 'unknown sender'}. Type what the certificate prints; a person still decides on
            the receipt screen.
          </SheetDescription>
        </SheetHeader>
        {open ? (
          <div className="grid min-h-0 flex-1 gap-(--s3) lg:grid-cols-2">
            <div className="flex min-h-[60vh] flex-col">
              <FileViewer path={`/cofa-intake/${row.id}/file`} label={row.attachmentName} title="QA@ attachment" />
            </div>
            <ByHandFormView row={row} onRecorded={onRecorded} />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

type SpecState = { kind: 'none' } | { kind: 'loading' } | { kind: 'loaded'; name: string } | { kind: 'absent'; why: string };

function ByHandFormView({ row, onRecorded }: { row: IntakeRow; onRecorded: () => void }) {
  const [form, setForm] = useState<ByHandForm>(() => emptyForm(row.receiptId));
  const [po, setPo] = useState(row.po ?? '');
  const emailPo = suggestedPo(row);
  const [lines, setLines] = useState<RegisterRow[] | null>(null);
  const [lineError, setLineError] = useState<string | null>(null);
  const [spec, setSpec] = useState<SpecState>({ kind: 'none' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const [reading, setReading] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const set = (patch: Partial<ByHandForm>) => setForm((f) => ({ ...f, ...patch }));

  // Read with AI (#152 step 4): the scan to the model; its answer fills the form as values to check.
  const readWithAi = async () => {
    setReading(true);
    setAiNote(null);
    try {
      const r = await api.post<{ suggestion: Suggestion }>(`/cofa-intake/${row.id}/prefill`);
      setForm((f) => applySuggestion(f, r.suggestion));
      // The PO the scan prints finds its line, when none was chosen yet (a person still confirms the line).
      const printed = /PO\s*-?\s*(\d{6})/i.exec(r.suggestion.poAsPrinted ?? '')?.[1];
      if (printed && form.receiptId === null && !po.trim()) {
        setPo(`PO${printed}`);
        findLines(`PO${printed}`);
      }
      setAiNote(
        r.suggestion.isCertificate
          ? 'Filled from the scan by AI. Check each outlined value against the scan: tick it, or correct it.'
          : `The AI does not read this as a certificate${r.suggestion.notCertificateReason ? ` (${r.suggestion.notCertificateReason})` : ''}. Anything it read is outlined to check.`,
      );
    } catch (err) {
      setAiNote(`Not read with AI: ${errorMessage(err)}`);
    } finally {
      setReading(false);
    }
  };
  const ai = (k: string) => ({
    unchecked: isUnchecked(form, k),
    name: k,
    onTick: () => setForm((f) => ({ ...f, confirmed: { ...f.confirmed, [k]: true } })),
  });

  const findLines = (p: string) => {
    setLineError(null);
    setLines(null);
    if (!p.trim()) return;
    api
      .get<RegisterPayload>(`/receipts?po=${encodeURIComponent(p.trim())}`)
      .then((r) => setLines(r.rows))
      .catch((err) => setLineError(errorMessage(err)));
  };
  useEffect(() => {
    if (row.po) findLines(row.po);
    // Once, for the line the intake job found (if any).
  }, []);

  // The material's spec in force: one row per COFA check, for the person to fill.
  useEffect(() => {
    const id = form.receiptId;
    if (id === null) return;
    const ctrl = new AbortController();
    setSpec({ kind: 'loading' });
    (async () => {
      try {
        const detail = await api.get<ReceiptDetail>(`/receipts/${id}`, ctrl.signal);
        const material = detail.material ?? null;
        if (!material) return setSpec({ kind: 'absent', why: 'No material is set up for this item, so there is no spec to fill from.' });
        try {
          const s = await api.get<{ parameters?: SpecParameter[] }>(`/specs?subjectType=MATERIAL&subjectId=${material.materialId}`, ctrl.signal);
          const rows = resultsFromSpec(s.parameters ?? []);
          setForm((f) => ({ ...f, results: [...rows, ...f.results.filter((r) => r.specParameterId === null)] }));
          setSpec({ kind: 'loaded', name: material.name });
        } catch (err) {
          setSpec({ kind: 'absent', why: `No approved spec for ${material.name} could be read (${errorMessage(err)}).` });
        }
      } catch (err) {
        if (!ctrl.signal.aborted) setSpec({ kind: 'absent', why: `The PO line could not be read: ${errorMessage(err)}.` });
      }
    })();
    return () => ctrl.abort();
  }, [form.receiptId]);

  const issues = problems(form);
  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const r = await api.post<{ cofaId?: number }>('/cofas', toBody(form, row.id));
      setSaved(r.cofaId ?? form.receiptId ?? 0);
      onRecorded();
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (saved !== null) {
    return (
      <div role="status" className="flex flex-col gap-(--s2) text-(length:--fs-sm)">
        <p className="m-0 font-semibold">Recorded as a manual read. It is judged against the spec; the decision is made on the receipt screen.</p>
        <Link to={`/office/goods-in/${form.receiptId}`} className="underline">
          Open the PO line to decide
        </Link>
      </div>
    );
  }

  const cell = 'h-8 text-(length:--fs-sm)';
  return (
    <form
      className="flex flex-col gap-(--s3) text-(length:--fs-sm)"
      onSubmit={(e) => {
        e.preventDefault();
        if (issues.length === 0 && !saving) void save();
      }}
    >
      <fieldset className="flex flex-col gap-(--s2)">
        <legend className="font-semibold">PO line</legend>
        <div className="flex gap-(--s2)">
          <Input aria-label="PO number" value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO number" className={cell} />
          <Button type="button" variant="outline" size="sm" onClick={() => findLines(po)}>
            Find
          </Button>
        </div>
        {/* (UI/UX audit, 31 Oct 2026, UX-48) The PO the email names, offered - never filled silently. */}
        {emailPo && !po.trim() ? (
          <p className="m-0 flex flex-wrap items-center gap-(--s2)">
            <span className="text-(--muted)">The email names {emailPo}.</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setPo(emailPo);
                findLines(emailPo);
              }}
            >
              Use {emailPo}
            </Button>
          </p>
        ) : null}
        {lineError ? <p role="alert" className="m-0">The PO could not be read: {lineError}</p> : null}
        {lines && lines.length === 0 ? <p className="m-0 text-(--muted)">No ingredient line on that PO in the register.</p> : null}
        {lines?.map((l) => (
          <label key={l.receiptId} className="flex items-center gap-(--s2)">
            <input type="radio" name="line" checked={form.receiptId === l.receiptId} onChange={() => set({ receiptId: l.receiptId })} />
            <span>
              {l.po} line {l.line} · {l.itemName ?? l.item} · {l.vendor.name ?? l.vendor.account ?? 'supplier not known'}
            </span>
          </label>
        ))}
      </fieldset>

      <div className="flex flex-col gap-(--s1) rounded-(--radius) border border-(--border) p-(--s2)">
        <div className="flex flex-wrap items-center gap-(--s2)">
          <Button type="button" variant="outline" size="sm" disabled={reading} onClick={() => void readWithAi()}>
            {reading ? 'Reading the scan…' : 'Read with AI'}
          </Button>
          <span className="text-(length:--fs-xs) text-(--muted)">
            Fills the form from the scan for you to check. Nothing is saved until every AI value is ticked or corrected.
          </span>
        </div>
        {aiNote ? (
          <p role="status" className="m-0 text-(length:--fs-xs) font-semibold">
            {aiNote} {uncheckedAi(form).length ? `${uncheckedAi(form).length} left to check.` : ''}
          </p>
        ) : null}
      </div>

      <fieldset className="flex flex-col gap-(--s2)">
        <legend className="font-semibold">Lots, exactly as printed</legend>
        {form.lots.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_9rem_9rem_auto] items-center gap-(--s2)">
            <AiField {...ai(`lot:${i}`)}>
              <Input aria-label={`Lot ${i + 1}`} value={l.lot} placeholder="Lot" className={cell} onChange={(e) => set({ lots: form.lots.map((x, j) => (j === i ? { ...x, lot: e.target.value } : x)) })} />
            </AiField>
            <AiField {...ai(`lot:${i}.made`)}>
              <Input aria-label={`Lot ${i + 1} produced`} type="date" value={l.manufacturedDate} className={cell} onChange={(e) => set({ lots: form.lots.map((x, j) => (j === i ? { ...x, manufacturedDate: e.target.value } : x)) })} />
            </AiField>
            <AiField {...ai(`lot:${i}.bb`)}>
              <Input aria-label={`Lot ${i + 1} best before`} type="date" value={l.bestBeforeDate} className={cell} onChange={(e) => set({ lots: form.lots.map((x, j) => (j === i ? { ...x, bestBeforeDate: e.target.value } : x)) })} />
            </AiField>
            <Button type="button" variant="ghost" size="sm" aria-label={`Remove lot ${i + 1}`} onClick={() => set({ lots: form.lots.length > 1 ? form.lots.filter((_, j) => j !== i) : [emptyLot()] })}>
              Remove
            </Button>
          </div>
        ))}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => set({ lots: [...form.lots, emptyLot()] })}>
            Add lot
          </Button>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-(--s2)">
        <legend className="font-semibold">Results, as printed</legend>
        {spec.kind === 'loading' ? <p className="m-0 text-(--muted)">Reading our spec…</p> : null}
        {spec.kind === 'loaded' ? <p className="m-0 text-(--muted)">One row per check of the {spec.name} spec. Leave blank what the certificate does not report.</p> : null}
        {spec.kind === 'absent' ? <p role="status" className="m-0">{spec.why} Type each result as printed.</p> : null}
        {form.results.length > 0 ? (
          <div className="grid grid-cols-[1.2fr_1fr_5rem_1fr_auto] items-center gap-(--s1)">
            <span className="text-(length:--fs-xs) text-(--muted)">Parameter</span>
            <span className="text-(length:--fs-xs) text-(--muted)">Result</span>
            <span className="text-(length:--fs-xs) text-(--muted)">Unit</span>
            <span className="text-(length:--fs-xs) text-(--muted)">Printed limit</span>
            <span />
            {form.results.map((r, i) => {
              const edit = (patch: Partial<typeof r>) => set({ results: form.results.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
              return [
                <Input key={`p${i}`} aria-label={`Result ${i + 1} parameter`} value={r.parameter} readOnly={r.specParameterId !== null} className={cell} onChange={(e) => edit({ parameter: e.target.value })} />,
                <AiField key={`r${i}`} {...ai(`result:${r.parameter}`)}>
                  <Input aria-label={`${r.parameter || `Result ${i + 1}`} result`} value={r.result} className={cell} onChange={(e) => edit({ result: e.target.value })} />
                </AiField>,
                <Input key={`u${i}`} aria-label={`${r.parameter || `Result ${i + 1}`} unit`} value={r.unit} className={cell} onChange={(e) => edit({ unit: e.target.value })} />,
                <Input key={`l${i}`} aria-label={`${r.parameter || `Result ${i + 1}`} printed limit`} value={r.printedLimit} className={cell} onChange={(e) => edit({ printedLimit: e.target.value })} />,
                r.specParameterId === null ? (
                  <Button key={`x${i}`} type="button" variant="ghost" size="sm" onClick={() => set({ results: form.results.filter((_, j) => j !== i) })}>
                    Remove
                  </Button>
                ) : (
                  <span key={`x${i}`} />
                ),
              ];
            })}
          </div>
        ) : null}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => set({ results: [...form.results, emptyResult()] })}>
            Add a result not in our spec
          </Button>
        </div>
      </fieldset>

      <fieldset className="grid gap-(--s2) sm:grid-cols-2">
        <legend className="col-span-full font-semibold">Issuer, product and signature, as printed</legend>
        <Label className="flex flex-col gap-(--s1)">
          Issuer
          <AiField {...ai('issuer')}>
            <Input value={form.issuer} className={cell} onChange={(e) => set({ issuer: e.target.value })} />
          </AiField>
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Product
          <AiField {...ai('product')}>
            <Input value={form.product} className={cell} onChange={(e) => set({ product: e.target.value })} />
          </AiField>
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Signed by
          <AiField {...ai('signature.kind')}>
            {/* (UI/UX audit, 31 Oct 2026, UX-13) 44px like the inputs around it; h-8 was 32px. */}
            <select
              value={form.signatureKind}
              className="min-h-[44px] w-full rounded-(--radius) border border-(--border) bg-transparent px-(--s2)"
              onChange={(e) => set({ signatureKind: e.target.value as SignatureKind | '' })}
            >
              <option value="">Choose…</option>
              {SIGNATURE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {SIGNATURE_LABEL[k]}
                </option>
              ))}
            </select>
          </AiField>
        </Label>
        <Label className="flex flex-col gap-(--s1)">
          Issued
          <AiField {...ai('issued')}>
            <Input type="date" value={form.issuedDate} className={cell} onChange={(e) => set({ issuedDate: e.target.value })} />
          </AiField>
        </Label>
        {form.signatureKind === 'PRINTED_NAME' || form.signatureKind === 'ELECTRONIC_STATEMENT' ? (
          <>
            <Label className="flex flex-col gap-(--s1)">
              Name as printed
              <AiField {...ai('signature.name')}>
                <Input value={form.signatureName} className={cell} onChange={(e) => set({ signatureName: e.target.value })} />
              </AiField>
            </Label>
            <Label className="flex flex-col gap-(--s1)">
              Role as printed
              <AiField {...ai('signature.role')}>
                <Input value={form.signatureRole} className={cell} onChange={(e) => set({ signatureRole: e.target.value })} />
              </AiField>
            </Label>
          </>
        ) : null}
      </fieldset>

      {issues.length > 0 ? (
        <ul className="m-0 list-disc pl-(--s4) text-(--muted)">
          {issues.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      {saveError ? (
        <p role="alert" className="m-0 font-semibold">
          Not recorded: {saveError}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={issues.length > 0 || saving}>
          {saving ? 'Recording…' : 'Record as a manual read'}
        </Button>
      </div>
    </form>
  );
}

/** An AI value still to check: outlined, with a tick that marks it checked against the scan (#152 step 4).
 *  Top-level, so the input inside keeps its focus while typing. Editing the value also clears it. */
function AiField({ unchecked, name, onTick, children }: { unchecked: boolean; name: string; onTick: () => void; children: React.ReactNode }) {
  if (!unchecked) return <>{children}</>;
  return (
    <div className="relative rounded-(--radius) ring-2 ring-(--warn-line)">
      {children}
      <button
        type="button"
        title="Checked against the scan"
        aria-label={`Mark the AI value of ${name} as checked against the scan`}
        className="absolute right-[4px] top-1/2 -translate-y-1/2 rounded-[4px] bg-(--warn-bg) px-[6px] text-(length:--fs-xs) font-semibold text-(--warn)"
        onClick={onTick}
      >
        ✓ AI
      </button>
    </div>
  );
}
